import { useCallback, useEffect, useMemo } from 'react';
import Grid from '@mui/material/Grid';
import { v4 as uuid } from 'uuid';

import BookOpenIcon from 'src/components/icons/BookOpen';
import useImpressionTracking from 'src/hooks/useImpressionTracking';
import {
  trackRelatedReadingCardViewed,
  trackRelatedReadingCtaClicked,
} from 'src/interviews/analytics';
import { trackRelatedReadings } from '../../analytics';
import { Paragraph } from 'src/interviews/components/Layout/Embedded';
import LoadingSkeleton from 'src/interviews/components/LoadingSkeleton';
import RelatedCard from '../RelatedCard';

import type { MiniInterviewType } from 'src/packages/polaris/types';

type RelatedReading = {
  categories: string[];
  overview: string;
  page_type: 'article' | 'blogPost';
  relevance: number;
  slug: string;
  title: string;
  url: string;
};

type Props = {
  readings: RelatedReading[];
  interviewToken: string;
  interviewType: MiniInterviewType;
  isLoading: boolean;
};

type RelatedReadingCardProps = {
  index: number;
  reading: RelatedReading;
  interviewType: MiniInterviewType;
};

const MAX_COUNT = 2;

function RelatedReadingCard(props: RelatedReadingCardProps) {
  const { index, reading, interviewType } = props;

  const impressionId = useMemo(() => uuid(), [reading.url]);

  const impressionRef = useImpressionTracking(() => {
    trackRelatedReadingCardViewed({
      impressionId,
      order: index,
      url: reading.url,
      interviewType,
    });
  });

  const handleClicked = useCallback(() => {
    trackRelatedReadingCtaClicked({
      impressionId,
      url: reading.url,
      interviewType,
    });
  }, [impressionId, interviewType, reading.url]);

  return (
    <RelatedCard
      ctaText="Read article"
      description={reading.overview}
      onCtaClick={handleClicked}
      href={reading.url}
      impressionRef={impressionRef}
      key={reading.url}
      subtitle={reading.categories?.[0] || 'Learn'}
      subtitleIcon={<BookOpenIcon className="bp-pr-1" />}
      title={reading.title}
    />
  );
}

function RelatedReadingComponent(props: Props) {
  const { readings = [], interviewToken, interviewType, isLoading } = props || {};

  const relatedReadings = readings.filter((reading) => reading?.url && reading?.title);

  useEffect(() => {
    if (!isLoading) {
      trackRelatedReadings(interviewToken, interviewType, relatedReadings);
    }
  }, [interviewToken, interviewType, isLoading, relatedReadings]);

  if (!relatedReadings?.length && !isLoading) {
    return null;
  }

  return (
    <Grid item className="bp-w-full">
      <Paragraph className="!bp-mb-3 !bp-font-bold">Related reading</Paragraph>

      {isLoading ? (
        <div className="bp-flex bp-h-[250px] bp-gap-3">
          {Array(MAX_COUNT)
            .fill(null)
            .map((_, index) => (
              <LoadingSkeleton key={`rq-loading-skeleton-${index}`} width="266px" height="100%" />
            ))}
        </div>
      ) : (
        <div className="bp-no-scrollbar bp-flex bp-gap-3 bp-overflow-x-scroll">
          {relatedReadings.map((reading, index) => (
            <RelatedReadingCard
              key={reading.url}
              index={index}
              reading={reading}
              interviewType={interviewType}
            />
          ))}
        </div>
      )}
    </Grid>
  );
}

export default RelatedReadingComponent;
export type RelatedReadingType = RelatedReading;
